import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AuthService from '../services/auth-service';

// Define the shape of our auth context
interface AuthContextType {
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  checkAuthStatus: () => void;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  checkAuthStatus: () => {},
});

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Function to check authentication status
  const checkAuthStatus = () => {
    // Add a small delay to ensure localStorage is updated
    setTimeout(() => {
      const authStatus = AuthService.isAuthenticated();
      setIsAuthenticated(authStatus);
    }, 100);
  };

  // Login function (called after successful OAuth)
  const login = () => {
    // Add a small delay to ensure localStorage is updated
    setTimeout(() => {
      checkAuthStatus();
    }, 100);
  };

  // Logout function
  const logout = async () => {
    await AuthService.logout();
    setIsAuthenticated(false);
  };

  // Check auth status on mount and set up interval
  useEffect(() => {
    // Check immediately on mount
    checkAuthStatus();
    
    // Set up an interval to periodically check auth status
    const interval = setInterval(checkAuthStatus, 1000);
    
    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, checkAuthStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;